import * as React from 'react';
import { useAtom } from 'jotai';

import AdminSettingsSection from 'src/components/AdminSettingsSection';
import Select from 'src/components/Select';
import { useSettingsQuery } from 'src/api/queries/userQueries';
import { useAssetSchemasListQuery } from 'src/api/queries/schemesQueries';
import { schemaSettingsAtom } from 'src/shared/settings/schema';
import { ACTION_TYPES } from 'src/shared/settings/base';
import { AssetSchema } from 'src/types/TSchemes';

import styles from './styles.module.scss';

const getLocalized = (obj?: { [lang: string]: string }): string => {
  if (!obj) return '';
  return obj.en || obj[Object.keys(obj)[0]] || '';
};

type DefaultUploadSchemaSettingProps = {
  className?: string;
};

/**
 * Account-wide "default schema for new uploads" control.
 *
 * Saved through the standard settings mutation (key `schema`, value
 * `{ default_upload_schema_id }`) — pair with `SaveChangesBar` on the page.
 * Uploads that do not specify a `data_schema_id` are assigned this schema;
 * when unset (Auto), the backend assigns the General Asset Schema preset.
 */
export const DefaultUploadSchemaSetting: React.FC<DefaultUploadSchemaSettingProps> = ({ className }) => {
  // Ensures the settings atoms are populated
  useSettingsQuery();
  const [schemaSettings, updateSchemaSettings] = useAtom(schemaSettingsAtom);
  const schemasQuery = useAssetSchemasListQuery('ACTIVE');

  // Exclude the built-in default schema (id 0): "Auto" covers the unset case
  const schemas: AssetSchema[] = (schemasQuery.data?.data ?? []).filter((s: AssetSchema) => s.id !== 0);

  const currentId = schemaSettings?.default_upload_schema_id;

  return (
    <AdminSettingsSection title="Default schema for new uploads" className={className}>
      <Select
        value={currentId ? String(currentId) : ''}
        onChange={(v) =>
          updateSchemaSettings({
            type: ACTION_TYPES.UPDATE_STATE,
            payload: { default_upload_schema_id: v ? Number(v) : null },
          })
        }
        options={[
          {
            label: schemasQuery.isLoading ? 'Loading schemas…' : 'Auto — General Asset Schema',
            value: '',
          },
          ...schemas.map((s) => ({
            label: getLocalized(s.name) || `Schema ${s.id}`,
            value: String(s.id),
          })),
        ]}
      />
      <p className={styles.description}>
        Newly uploaded assets are automatically assigned this schema when the upload does not
        specify one. With Auto, the General Asset Schema preset is used and added to your account
        if it does not exist yet.
      </p>
    </AdminSettingsSection>
  );
};

export default DefaultUploadSchemaSetting;
